import { STRING } from "@constants";
import { LocationRead } from "@types";

const T = STRING.LOCATION_DETAILS;

export function getLocationDetails(data?: LocationRead | null) {
  if (!data) return { sections: [] };

  const sections: any[] = [];

  // Combined photos section with primary image and gallery
  const hasPhotos = data.homePhotoUrl || (data.photos && data.photos.length > 0);

  if (hasPhotos) {
    const allImages = [];
    const usedUrls = new Set();

    // Add primary location image first
    if (data.homePhotoUrl) {
      allImages.push({
        src: data.homePhotoUrl,
        isPrimary: true,
        title: "Primary Location Image",
        description: "Main image shown in listings"
      });
      usedUrls.add(data.homePhotoUrl);
    }

    // Add gallery photos (excluding duplicates)
    if (data.photos && data.photos.length > 0) {
      // Filter out photos that are already shown as homePhotoUrl
      const uniquePhotos = data.photos.filter(p => !usedUrls.has(p.url));

      uniquePhotos.forEach((photo, index) => {
        allImages.push({
          src: photo.url,
          isFeatured: photo.isMain,
          title: photo.isMain ? "Featured Gallery Photo" : `Gallery Photo ${index + 1}`,
          description: photo.isMain ? "Featured photo from gallery" : "Additional location view"
        });
      });
    }

    sections.push({
      type: "photos",
      title: "Location Photos",
      icon: "pi-images",
      primaryImage: data.homePhotoUrl,
      images: allImages,
      totalCount: allImages.length
    });
  }

  sections.push(
    {
      title: data.locationName ?? T.UNNAMED_LOCATION,
      icon: "pi-map-marker",
      fields: [
        { label: T.ADDRESS_NAME, value: data.addressName },
        { label: T.ADDRESS, value: data.address },
        { label: T.ADDRESS2, value: data.address2 },
        { label: T.CITY, value: data.city },
        { label: T.STATE, value: data.state },
        { label: T.ZIP_CODE, value: data.zipCode },
        { label: T.EMAIL, value: data.email },
        { label: T.PHONE, value: data.phone },
        { label: T.TYPE, value: data.type },
      ],
    },
    {
      title: "📅 Business Hours",
      icon: "pi-clock",
      fields: [
        {
          label: "📆 Mon-Fri",
          value: data.hoursWeekdays?.trim() || "Closed",
          status: (data.hoursWeekdays && data.hoursWeekdays.trim() !== "" && data.hoursWeekdays.toLowerCase() !== "closed") ? "open" : "closed"
        },
        {
          label: "📅 Saturday",
          value: data.hoursSaturday?.trim() || "Closed",
          status: (data.hoursSaturday && data.hoursSaturday.trim() !== "" && data.hoursSaturday.toLowerCase() !== "closed") ? "open" : "closed"
        },
        {
          label: "📅 Sunday",
          value: data.hoursSunday?.trim() || "Closed",
          status: (data.hoursSunday && data.hoursSunday.trim() !== "" && data.hoursSunday.toLowerCase() !== "closed") ? "open" : "closed"
        },
        {
          label: "🚪 Gate Hours",
          value: (data.gateHours && data.gateHours.trim() !== "")
            ? data.gateHours
            : data.hoursWeekdays?.trim() || "Closed",
          status: "info"
        },
      ],
    },
    {
      title: T.LOCATION_COORDINATES,
      icon: "pi-compass",
      fields: [
        { label: T.LATITUDE, value: data.latitude },
        { label: T.LONGITUDE, value: data.longitude },
      ],
    },
    {
      title: T.OWNER_INFO,
      icon: "pi-user",
      fields: [
        { label: T.OWNER_NAME, value: data.ownerName },
      ],
    },
    {
      title: T.PROCESSOR_INFO,
      icon: "pi-cog",
      fields: [
        { label: T.PROCESSOR_ID, value: data.processorCustomerId },
        { label: T.PROCESSOR_TYPE, value: data.processorType },
        { label: T.PROCESSOR_PASSWORD, value: data.processorPassword },
      ],
    },
  );

  return { sections };
}
